import { mapConfig } from 'lib/config'
import { SearchServiceResultItem } from 'map/services'

/*We have 'categoryQuery' as RecentItemType which is not being used for now but it will be used in future for search along the route with
category search*/
export enum RecentItemType {
  place = 'place',
  freeText = 'freeText',
  categoryQuery = 'categoryQuery',
  chainQuery = 'chainQuery',
  currentLocation = 'currentLocation',
  locality = 'locality',
}

export interface SerializedRecentItem extends RecentItem {
  placeId: string
  title: string
  type: RecentItemType
  timestamp: string
}

export interface RecentItem {
  placeId: string
  title: string
  resultType?: string
  placeCategoryId?: string
  address?: string
  lat?: number
  lon?: number
  distance?: number
  distanceLabel?: string
}

enum RecentListPosition {
  notFound = -1,
  topOnList = 0,
  inList = 1,
}

export enum UnitType {
  automatic = 'automatic',
  metric = 'metric',
  imperialUK = 'imperialUK',
  imperialUS = 'imperialUS',
}

export type RoutePreferenceKey =
  | 'short'
  | 'uTurns'
  | 'ferry'
  | 'controlledAccessHighway'
  | 'tunnel'
  | 'tollRoad'
  | 'dirtRoad'

type RoutePreferenceModes = 'car' | 'pedestrian' | 'bicycle'
type RoutePreferences = {
  [key in RoutePreferenceModes]: RoutePreferenceKey[]
}

export const recentStorageKey = `${mapConfig.storageRootKey}:recents`
export const unitStorageKey = `${mapConfig.storageRootKey}:units`
export const routePreferencesStorageKey = `${mapConfig.storageRootKey}:route-preferences`

function findListItemPositionAndIndex(
  recentItem: SerializedRecentItem,
  storedRecentItemList: SerializedRecentItem[]
): [RecentListPosition, number] {
  const hasSamePlaceId = (storedRecentItem: SerializedRecentItem): boolean =>
    recentItem.placeId !== '' && recentItem.placeId === storedRecentItem.placeId

  const hasSameTitleAndType = (storedRecentItem: SerializedRecentItem): boolean =>
    recentItem.title === storedRecentItem.title && recentItem.type === storedRecentItem.type

  const indexValue = storedRecentItemList.findIndex(
    (listItem) => hasSamePlaceId(listItem) || hasSameTitleAndType(listItem)
  )

  if (indexValue > 0) {
    return [RecentListPosition.inList, indexValue]
  }
  return [indexValue as RecentListPosition, indexValue]
}

export function removeRecentItem(itemId: string): void {
  const recentList = getRecentList()
  const recentItemIndex = recentList.findIndex((item) => item.placeId === itemId)
  recentList.splice(recentItemIndex, 1)
  localStorage.setItem(recentStorageKey, JSON.stringify(recentList.slice(0, 20)))
}

// TODO That should be removed after refactoring
export function storeInRecentList(item: SerializedRecentItem): void {
  const recentList = spliceInRecentList(item, getRecentList())

  localStorage.setItem(recentStorageKey, JSON.stringify(recentList.slice(0, 20)))
}

export function updateRecentList(recentList: SerializedRecentItem[]): void {
  localStorage.setItem(recentStorageKey, JSON.stringify(recentList.slice(0, 20)))
}

export function getRecentList(): SerializedRecentItem[] {
  const localStorageValue = localStorage.getItem(recentStorageKey)
  const storedRecentItems = JSON.parse(
    localStorageValue !== null ? localStorageValue : '[]'
  ) as SerializedRecentItem[]

  return storedRecentItems.length > 0 ? [...storedRecentItems] : []
}

/**
 * Function that splice a recent item in the list
 *
 * If received searched item is already present in stored recent list at index > 0 then
 * we delete that item from that particular index and add it again on top ie. at 0th index else
 * we will not add or remove the item in stored list as it is being present on top of the list which means it is already
 * the recently searched item
 *
 * @param item The SerializedRecentItem you want to splice in the list
 * @param recentList The SerializedRecentItem list in which item will get spliced
 **/
export function spliceInRecentList(
  item: SerializedRecentItem,
  recentList: SerializedRecentItem[]
): SerializedRecentItem[] {
  const newRecentList = [...recentList]
  const [position, index] = findListItemPositionAndIndex(item, recentList)

  switch (position) {
    case RecentListPosition.inList:
      newRecentList.splice(index, 1)
      newRecentList.unshift(item)
      return newRecentList
    case RecentListPosition.notFound:
      newRecentList.unshift(item)
      return newRecentList
    case RecentListPosition.topOnList:
    default:
      return recentList
  }
}

export function createSerializedRecentItem(
  type: RecentItemType,
  searchPhrase?: string,
  item?: RecentItem
): SerializedRecentItem {
  const recentItem = {
    placeId: new Date().toISOString(),
    type,
    ...(searchPhrase !== undefined ? { title: searchPhrase } : { title: '' }),
    timestamp: new Date().toISOString(),
  }
  if (item) {
    return {
      ...recentItem,
      ...item,
    }
  }
  return recentItem
}

// TODO to be removed in the future
export function fromSearchServiceResultItemToRecentItem(item: SearchServiceResultItem): RecentItem {
  const placeId = item.id
  const resultType = item.resultType
  const title = item.title || ''
  const distance = ('distance' in item && item.distance) || undefined
  const placeCategoryId =
    ('categories' in item &&
      item.categories.length &&
      item.categories.filter((item) => item.primary)[0].id) ||
    undefined
  const address = ('address' in item && item.address.label) || undefined
  const { lat, lon } =
    'position' in item
      ? {
          lat: item.position.lat,
          lon: item.position.lng,
        }
      : { lat: undefined, lon: undefined }

  return {
    address,
    distance,
    lat,
    lon,
    placeCategoryId,
    placeId,
    resultType,
    title,
  }
}

export function fetchUnit(): string {
  const unitStorageValue = localStorage.getItem(unitStorageKey)
  if (unitStorageValue !== null && unitStorageValue !== undefined) {
    return unitStorageValue
  }

  updateUnit(UnitType.automatic)
  return UnitType.automatic
}

export function updateUnit(unit: string): void {
  localStorage.setItem(unitStorageKey, unit)
}

export function getRoutePreferences(selectedTransportMode: string): RoutePreferenceKey[] {
  const allRoutePreferences = getAllRoutePreferences()
  const routePreferenceslist = allRoutePreferences[selectedTransportMode as RoutePreferenceModes]

  return Array.from(new Set(routePreferenceslist))
}

function getAllRoutePreferences(): RoutePreferences {
  const routePreferencesString = localStorage.getItem(routePreferencesStorageKey)
  const allRoutePreferences = JSON.parse(
    routePreferencesString !== null && routePreferencesString !== '' ? routePreferencesString : '{}'
  ) as RoutePreferences
  return Array.isArray(allRoutePreferences) ? ({} as RoutePreferences) : allRoutePreferences
}

export function updateRoutePreferences(
  routePreferences: RoutePreferenceKey[],
  selectedTransportMode: string
): void {
  const allRoutePreferences = getAllRoutePreferences()
  allRoutePreferences[selectedTransportMode as RoutePreferenceModes] = routePreferences

  localStorage.setItem(routePreferencesStorageKey, JSON.stringify(allRoutePreferences))
}
